import LoadingScreen from 'pricing-pages-ui-components/components/LoadingScreen';
import { createToObject } from 'pricing-pages-ui-components/utils/queryParamUtils';
import { HubSpotOnCreditsPage, BundlePage as LegacyBundlePage } from 'pricing-pages-ui/routes/loadablePages';
import { Redirect, Route, Switch } from 'quartz-routing/react-router';
import { useIsHubSpotOnCredits } from './hooks/useIsHubSpotOnCredits';
import { useIsPublicBundlePageEnabled } from './hooks/useIsPublicBundlePageEnabled';
import { useIsRevenueHubEnabled } from './hooks/useIsRevenueHubEnabled';
import PublicAppLayout from './layouts/PublicAppLayout';
import PublicCommerceHubPage from './pages/commerce/PublicCommerceHubPage';
import PublicContentHubPage from './pages/content/PublicContentHubPage';
import PublicDataHubPage from './pages/data/PublicDataHubPage';
import PublicFreeToolsPage from './pages/free/PublicFreeToolsPage';
import PublicMarketingPlusPage from './pages/marketing-plus/PublicMarketingPlusPage';
import PublicMarketingHubPage from './pages/marketing/PublicMarketingHubPage';
import PublicSalesHubPage from './pages/sales/PublicSalesHubPage';
import PublicServiceHubPage from './pages/service/PublicServiceHubPage';
import PublicSmartCrmPage from './pages/smart-crm/PublicSmartCrmPage';
import PublicPlatformPage from './pages/suite/PublicPlatformPage';
import { createDeferredLoadable } from './utils/createDeferredLoadable';
import { getUseCasesByPage, useCasesQueryParamKey } from './utils/hsoc';

/*
 * The create-a-bundle page is the heaviest route in the public app (the full
 * plan-builder, ~13.5k lines). Static-importing it would pull all of it into
 * the main bundle and slow every route, so it is code-split into its own chunk
 * via the sanctioned createDeferredLoadable primitive. This deviates from
 * ARCHITECTURE.md §3 (thin route shells are inlined), which is calibrated for
 * ~4KB hub shells; a page this heavy is the case §11 (no large eager imports)
 * is meant to catch.
 */
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const PublicBundlePage = createDeferredLoadable(() => import( /* webpackChunkName: "public-bundle-page" */'./pages/bundle/PublicBundlePage').then(m => ({
  default: m.default
})));
export default function PublicRoutes() {
  const {
    isHubSpotOnCredits,
    loading: isHSoCPilotLoading
  } = useIsHubSpotOnCredits();
  const isPublicBundlePageEnabled = useIsPublicBundlePageEnabled();
  const isRevenueHubEnabled = useIsRevenueHubEnabled();
  if (isHSoCPilotLoading) {
    return /*#__PURE__*/_jsx(LoadingScreen, {});
  }

  // HSOC is a full-width experience, no sidenav layout.
  if (isHubSpotOnCredits) {
    return /*#__PURE__*/_jsxs(Switch, {
      children: [/*#__PURE__*/_jsx(Route, {
        path: "/",
        exact: true,
        component: HubSpotOnCreditsPage
      }), /*#__PURE__*/_jsx(Route, {
        path: "/:page",
        render: ({
          location,
          match: {
            params: {
              page
            }
          }
        }) => {
          const searchParams = new URLSearchParams(location.search);
          const useCases = getUseCasesByPage(page);
          for (const useCase of useCases) {
            searchParams.append(useCasesQueryParamKey, useCase);
          }
          return /*#__PURE__*/_jsx(Redirect, {
            to: Object.assign({}, location, {
              search: searchParams.toString(),
              pathname: '/'
            })
          });
        }
      })]
    });
  }
  return /*#__PURE__*/_jsx(PublicAppLayout, {
    children: /*#__PURE__*/_jsxs(Switch, {
      children: [/*#__PURE__*/_jsx(Route, {
        path: "/customer-platform",
        children: /*#__PURE__*/_jsx(Redirect, {
          to: createToObject('/suite')
        })
      }), /*#__PURE__*/_jsx(Route, {
        path: "/cms",
        children: /*#__PURE__*/_jsx(Redirect, {
          to: createToObject('/content')
        })
      }), /*#__PURE__*/_jsx(Route, {
        path: "/hubspot-credits",
        children: /*#__PURE__*/_jsx(Redirect, {
          to: createToObject('/')
        })
      }), /*#__PURE__*/_jsx(Route, {
        path: "/bundle",
        render: () => isPublicBundlePageEnabled ? /*#__PURE__*/_jsx(PublicBundlePage, {}) : /*#__PURE__*/_jsx(LegacyBundlePage, {})
      }), /*#__PURE__*/_jsx(Route, {
        path: "/marketing-plus",
        render: () => /*#__PURE__*/_jsx(PublicMarketingPlusPage, {})
      }), /*#__PURE__*/_jsx(Route, {
        path: "/crm",
        render: () => /*#__PURE__*/_jsx(PublicFreeToolsPage, {})
      }), /*#__PURE__*/_jsx(Route, {
        path: "/suite",
        render: () => /*#__PURE__*/_jsx(PublicPlatformPage, {})
      }), /*#__PURE__*/_jsx(Route, {
        path: "/marketing",
        render: () => /*#__PURE__*/_jsx(PublicMarketingHubPage, {})
      }), /*#__PURE__*/_jsx(Route, {
        path: "/sales",
        render: () => /*#__PURE__*/_jsx(PublicSalesHubPage, {})
      }), /*#__PURE__*/_jsx(Route, {
        path: "/service",
        render: () => /*#__PURE__*/_jsx(PublicServiceHubPage, {})
      }), /*#__PURE__*/_jsx(Route, {
        path: "/content",
        render: () => /*#__PURE__*/_jsx(PublicContentHubPage, {})
      }), /*#__PURE__*/_jsx(Route, {
        path: "/data",
        render: () => /*#__PURE__*/_jsx(PublicDataHubPage, {})
      }), /*#__PURE__*/_jsx(Route, {
        path: "/revenue",
        render: () => isRevenueHubEnabled ? /*#__PURE__*/_jsx(PublicCommerceHubPage, {}) : /*#__PURE__*/_jsx(Redirect, {
          to: createToObject('/commerce')
        })
      }), /*#__PURE__*/_jsx(Route, {
        path: "/commerce",
        render: () => isRevenueHubEnabled ? /*#__PURE__*/_jsx(Redirect, {
          to: createToObject('/revenue')
        }) : /*#__PURE__*/_jsx(PublicCommerceHubPage, {})
      }), /*#__PURE__*/_jsx(Route, {
        path: "/smart-crm",
        render: () => /*#__PURE__*/_jsx(PublicSmartCrmPage, {})
      }), /*#__PURE__*/_jsx(Redirect, {
        from: "/",
        exact: true,
        to: createToObject('/marketing')
      })]
    })
  });
}